/** Aba "Inspeções": medições por pneu, com foto e o vínculo com a O.S. quando houver. */
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { TireInspectionRowDto } from "@bi/types";
import { api } from "../../../lib/api";
import { EmptyState } from "../../../components/EmptyState";
import { IconEye } from "../../../components/icons";
import { EvidenceThumbs } from "../../maintenance/EvidenceThumbs";
import { ConditionBadge, fmtDateTime, fmtMm, fmtPsi, useTireCapabilities } from "../tire-ui";

export function TireInspectionsTab() {
  const { can } = useTireCapabilities();
  const [params, setParams] = useSearchParams();
  const condition = params.get("condicao") ?? "";
  const plate = params.get("placa") ?? "";
  const set = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p, { replace: true });
  };

  const q = useQuery({
    queryKey: ["tires", "inspections", condition, plate],
    queryFn: () => {
      const p = new URLSearchParams({ limit: "200" });
      if (condition) p.set("condition", condition);
      if (plate) p.set("plate", plate);
      return api.get<{ total: number; rows: TireInspectionRowDto[] }>(`/fleet/tire-inspections?${p}`);
    },
  });

  return (
    <div className="space-y-4">
      <div className="card p-3 flex flex-wrap gap-2 items-center">
        <select className="input w-40" value={condition} onChange={(e) => set("condicao", e.target.value)}>
          <option value="">Toda condição</option>
          <option value="critical">Crítico</option>
          <option value="attention">Atenção</option>
          <option value="normal">Normal</option>
        </select>
        <input className="input w-32 uppercase font-mono" placeholder="Veículo" defaultValue={plate}
          onBlur={(e) => set("placa", e.target.value.trim().toUpperCase())}
          onKeyDown={(e) => { if (e.key === "Enter") set("placa", (e.target as HTMLInputElement).value.trim().toUpperCase()); }} />
        {can("tires.inspect") && (
          <Link className="btn-primary ml-auto" to={`/app/frota/pneus/inspecao${plate ? `?placa=${encodeURIComponent(plate)}` : ""}`}>Nova inspeção</Link>
        )}
      </div>
      <div className="card overflow-x-auto">
        {q.isLoading ? <p className="p-6 text-sm text-gray-500">Carregando…</p>
          : (q.data?.rows ?? []).length === 0 ? (
            <EmptyState icon={<IconEye className="w-5 h-5" />} title="Nenhuma inspeção registrada"
              description="A ronda de inspeção (sulco, pressão e condição por posição) é feita pelo app, em Pneus › Inspecionar." />
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-surface-muted text-xs text-gray-500 uppercase">
                <tr>
                  <th className="text-left px-4 py-2">Data</th>
                  <th className="text-left px-4 py-2">Pneu</th>
                  <th className="text-left px-4 py-2">Veículo / posição</th>
                  <th className="text-right px-4 py-2">Sulco</th>
                  <th className="text-right px-4 py-2">Pressão</th>
                  <th className="text-left px-4 py-2">Condição</th>
                  <th className="text-left px-4 py-2 hidden lg:table-cell">Problemas</th>
                  <th className="text-left px-4 py-2 hidden md:table-cell">Por</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {q.data!.rows.map((i) => (
                  <tr key={i.id} className="align-top">
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">{fmtDateTime(i.inspectedAt)}</td>
                    <td className="px-4 py-3 font-mono"><Link className="hover:underline" to={`/frota/pneus/${i.tireId}`}>{i.tireCode}</Link></td>
                    <td className="px-4 py-3 text-gray-600">{i.plate ?? "—"}{i.positionLabel ? ` · ${i.positionLabel}` : ""}</td>
                    <td className="px-4 py-3 text-right">{fmtMm(i.treadDepthMm)}</td>
                    <td className="px-4 py-3 text-right">{fmtPsi(i.pressurePsi)}</td>
                    <td className="px-4 py-3">
                      <ConditionBadge condition={i.condition} />
                      {i.workOrderId && <Link className="ml-2 text-xs text-tm-700 hover:underline" to={`/frota/manutencao/ordens/${i.workOrderId}`}>O.S.</Link>}
                    </td>
                    <td className="px-4 py-3 text-gray-600 hidden lg:table-cell">
                      {i.problems.map((p) => p.label).join(", ") || "—"}
                      {i.notes && <p className="text-xs text-gray-400">{i.notes}</p>}
                      <EvidenceThumbs anexos={i.attachments} size="sm" />
                    </td>
                    <td className="px-4 py-3 text-gray-500 hidden md:table-cell">{i.by ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
    </div>
  );
}
